import { useEffect, useState } from "react";
import { Calculator, Wrench, Send, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { StatusBadge } from "../DashboardShell";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";

const empty = { client_name: "", machine_id: "", lease_months: 12, monthly_kegs: 10, margin_per_keg: 20, notes: "" };

export default function MobileComodato() {
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  const [list, setList] = useState([]);
  const [com, setCom] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/machines"), api.get("/clients"), api.get("/comodati")])
    .then(([m, c, l]) => { setMachines(m.data); setClients(c.data); setList(l.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const machine = machines.find((m) => m.id === com.machine_id);
  const absorption = Number(com.monthly_kegs) * Number(com.margin_per_keg);
  const breakeven = machine && absorption > 0 ? Math.ceil(machine.cost / absorption) : null;
  const okInLease = breakeven !== null && breakeven <= Number(com.lease_months);

  const submit = async () => {
    if (!com.client_name || !com.machine_id) { toast.error("Scegli cliente e impianto"); return; }
    setBusy(true);
    try {
      await api.post("/comodati", { ...com, lease_months: Number(com.lease_months), monthly_kegs: Number(com.monthly_kegs), margin_per_keg: Number(com.margin_per_keg) });
      toast.success("Richiesta comodato inviata"); setCom(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="agent-mobile-comodato">
      <h1 className="font-serif text-2xl font-semibold text-[#0B2A45] mb-3 flex items-center gap-2"><Wrench className="w-6 h-6 text-[#C2593F]" />Comodato impianto</h1>
      <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-3">
        <div><Label>Cliente *</Label>
          <Select value={com.client_name} onValueChange={(v) => setCom({ ...com, client_name: v })}>
            <SelectTrigger className="h-12 mt-1" data-testid="mobile-comodato-client"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}{c.city && ` — ${c.city}`}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Impianto *</Label>
          <Select value={com.machine_id} onValueChange={(v) => setCom({ ...com, machine_id: v })}>
            <SelectTrigger className="h-12 mt-1" data-testid="mobile-comodato-machine"><SelectValue placeholder="Seleziona impianto" /></SelectTrigger>
            <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {eur(m.cost)}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Mesi</Label><Input type="number" inputMode="numeric" value={com.lease_months} onChange={(e) => setCom({ ...com, lease_months: e.target.value })} className="h-11 mt-1" data-testid="mobile-comodato-months" /></div>
          <div><Label className="text-xs">Fusti/mese</Label><Input type="number" inputMode="numeric" value={com.monthly_kegs} onChange={(e) => setCom({ ...com, monthly_kegs: e.target.value })} className="h-11 mt-1" data-testid="mobile-comodato-kegs" /></div>
          <div><Label className="text-xs">Margine/fusto €</Label><Input type="number" inputMode="decimal" value={com.margin_per_keg} onChange={(e) => setCom({ ...com, margin_per_keg: e.target.value })} className="h-11 mt-1" data-testid="mobile-comodato-margin" /></div>
        </div>
        {machine && (
          <div className={`rounded-xl p-4 flex items-center gap-3 ${okInLease ? "bg-[#DCFCE7]" : "bg-[#FFEDD5]"}`} data-testid="mobile-comodato-amortization">
            <Calculator className={`w-8 h-8 ${okInLease ? "text-[#15803D]" : "text-[#9A3412]"}`} />
            <div className="text-sm">
              <div className="text-xs text-stone-600">Assorbimento {eur(absorption)}/mese · Impianto {eur(machine.cost)}</div>
              <div className={`font-semibold ${okInLease ? "text-[#15803D]" : "text-[#9A3412]"}`} data-testid="mobile-comodato-breakeven">Ammortamento in {breakeven ?? "—"} mesi {breakeven !== null && (okInLease ? "· rientra nel contratto" : `· oltre i ${com.lease_months} mesi`)}</div>
            </div>
          </div>
        )}
        <Textarea value={com.notes} onChange={(e) => setCom({ ...com, notes: e.target.value })} placeholder="Note (posizione, spazio, esigenze…)" rows={2} />
        <button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl bg-[#C2593F] text-white font-bold flex items-center justify-center gap-2" data-testid="mobile-comodato-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Invia richiesta</>}</button>
      </div>

      <h2 className="font-serif text-lg font-semibold text-[#0B2A45] mt-6 mb-2">Le mie richieste <span className="text-stone-400 text-sm">({list.length})</span></h2>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-stone-400">Nessuna richiesta.</p>}
        {list.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-stone-200 p-3 flex items-center justify-between gap-3" data-testid={`mobile-comodato-${c.id}`}>
            <div className="min-w-0"><div className="font-semibold text-[#0B2A45] truncate">{c.client_name}</div><div className="text-xs text-stone-500 truncate">{c.machine_name} · {c.lease_months} mesi · ammort. {c.breakeven_months ?? "—"} mesi</div></div>
            <StatusBadge status={c.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
